// axios.config.ts

import axios, {
 AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig
} from 'axios';

// Type definitions for interceptor functions
type RequestInterceptor = (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>;
type ResponseInterceptor = (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>;
type RequestErrorInterceptor = (error: AxiosError) => Promise<never>;
type ResponseErrorInterceptor = (error: AxiosError) => Promise<never>;

// Default interceptor functions with proper typing
const defaultOnRequestIntercept: RequestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => config;
const defaultOnResponseIntercept: ResponseInterceptor = (response: AxiosResponse): AxiosResponse => response;

const defaultOnRequestError: RequestErrorInterceptor = (error: AxiosError): Promise<never> => Promise.reject(error);
const defaultOnResponseError: ResponseErrorInterceptor = (error: AxiosError): Promise<never> => Promise.reject(error);

/**
 * A Dynamic API client maker with request and response interceptors.
 * @param baseURL - The base URL for the API.
 * @param endpoint - The API endpoint.
 * @param onRequest - Optional function to be called on request intercept.
 * @param onResponse - Optional function to be called on response intercept.
 * @param onRequestError - Optional function to be called on request error.
 * @param onResponseError - Optional function to be called on response error.
 * @returns The Axios instance for making API requests.
 */
export const createApiClient = (
    baseURL: string,
    endpoint: string,
    onRequest: RequestInterceptor = defaultOnRequestIntercept,
    onResponse: ResponseInterceptor = defaultOnResponseIntercept,
    onRequestError: RequestErrorInterceptor = defaultOnRequestError,
    onResponseError: ResponseErrorInterceptor = defaultOnResponseError
): AxiosInstance => {
    /**
     * Axios instance for making API requests.
     */
    const apiClient: AxiosInstance = axios.create({
        baseURL: `${baseURL}${endpoint}`,
        withCredentials: true,
        headers: {
            'Content-type': 'application/json'
        }
    });

    /**
     * Request interceptor for the API client.
     * @see {@link https://axios-http.com/docs/interceptors}
     * @param config - The request configuration.
     * @returns The modified request configuration.
     */
    apiClient.interceptors.request.use(
        (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> => onRequest(config),
        (error: AxiosError): Promise<never> => onRequestError(error)
    );

    /**
     * Response interceptor for the API client.
     * @see {@link https://axios-http.com/docs/interceptors}
     * @param response - The response received from the API.
     * @returns The unmodified response.
     */
    apiClient.interceptors.response.use(
        (response: AxiosResponse): AxiosResponse | Promise<AxiosResponse> => onResponse(response),
        (error: AxiosError): Promise<never> => onResponseError(error)
    );

    return apiClient;
};
